import { ChangeEvent, useContext, useMemo, useState } from "react";
import {
  CreateLinkPayload,
  LinkListItem,
  LinkSectionEnum,
  LinkTypeEnum,
  UpdateLinkPayload,
} from "../../api/types/link";
import { useMutation, useQueryClient } from "react-query";
import { createProfileLinkRequest } from "../../api/queries/links/create";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { queryKeys } from "../../api/query";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { deleteLinkRequest } from "../../api/queries/links/delete";
import { updateLinkRequest } from "../../api/queries/links/update";
import { userContext } from "../../context/user";
import { TagListItem } from "../../api/types/tag";
import { useAppToast } from "../../context/toast";
import debounce from "lodash.debounce";
import { updateLinkTagsRequest } from "../../api/queries/people/updateLinkTags";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { TranscriptModal } from "../Common/TranscriptModal";

export const Links = ({ uuid, links, section, availableTags }: Props) => {
  const queryClient = useQueryClient();
  const { isAccountManager } = useContext(userContext);
  const linkTypes: Array<{ value: LinkTypeEnum; label: string }> = [
    { value: LinkTypeEnum.ARTICLE, label: "Article" },
    { value: LinkTypeEnum.LINKEDIN, label: "Linkedin" },
    { value: LinkTypeEnum.ELITE_PROSPECTS, label: "Elite Prospects" },
    { value: LinkTypeEnum.VIMEO, label: "Vimeo" },
    { value: LinkTypeEnum.YOUTUBE, label: "Youtube" },
    { value: LinkTypeEnum.SPOTIFY, label: "Spotify" },
  ];
  const linkTypeOptions = [...linkTypes].filter((lt) =>
    section === LinkSectionEnum.PROFILE
      ? true
      : lt.value !== LinkTypeEnum.LINKEDIN &&
        lt.value !== LinkTypeEnum.ELITE_PROSPECTS,
  );

  const { ref: appToastRef } = useAppToast();

  const [tagsByLinkId, setTagsByLinkId] = useState<
    Record<string, TagListItem[]>
  >({});

  const [type, setType] = useState<LinkTypeEnum>(LinkTypeEnum.ARTICLE);
  const onTypeChange = (event: DropdownChangeEvent) => setType(event.value);
  const [value, setValue] = useState<string>("");
  const onLinkChange = (event: ChangeEvent<HTMLInputElement>) =>
    setValue(event.target.value);
  const [updateLinkId, setUpdateLinkId] = useState<string | undefined>();

  const [transcriptModalVisible, setTranscriptModalVisible] = useState(false);
  const [selectedLinkForTranscript, setSelectedLinkForTranscript] =
    useState<LinkListItem | null>(null);

  const showTranscript = (link: LinkListItem) => {
    setSelectedLinkForTranscript(link);
    setTranscriptModalVisible(true);
  };

  const hideTranscriptModal = () => {
    setTranscriptModalVisible(false);
    setSelectedLinkForTranscript(null);
  };

  const updateTagsMutation = useMutation({
    mutationFn: ({ id, tags }: { id: string; tags: TagListItem[] }) =>
      updateLinkTagsRequest(id, { tags: tags.map((tag) => tag.id) }),
    onSuccess: () => {
      appToastRef?.current?.show({
        severity: "success",
        summary: "Tags updated successfully.",
      });

      queryClient.invalidateQueries(["links", section, uuid]);
    },
    onError: (error: any) => {
      appToastRef?.current?.show({
        severity: "error",
        summary: `Update failed: ${error.message}`,
      });
    },
  });
  const debouncedUpdateTags = useMemo(() => {
    return debounce((id: string, tags: TagListItem[]) => {
      updateTagsMutation.mutate({ id, tags });
    }, 500);
  }, [updateTagsMutation]);

  const onTagsChange = (e: MultiSelectChangeEvent) => {
    if (!updateLinkId) return;
    const newTags = e.value;
    setTagsByLinkId((prev) => ({
      ...prev,
      [updateLinkId]: newTags,
    }));
    debouncedUpdateTags(updateLinkId, newTags);
  };

  const createLink = useMutation({
    mutationFn: (payload: CreateLinkPayload) =>
      createProfileLinkRequest(section, uuid, payload),
  });

  const updateLink = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLinkPayload }) =>
      updateLinkRequest(id, payload),
  });

  const deleteLink = useMutation({
    mutationFn: (id: string) => deleteLinkRequest(id),
  });

  const onSubmit = async () => {
    const payload: CreateLinkPayload = {
      value,
      type,
    };

    if (updateLinkId) {
      await updateLink.mutateAsync({
        id: updateLinkId,
        payload: {
          type,
          value,
        },
      });

      setUpdateLinkId(undefined);
      setTagsByLinkId((prev) => ({
        ...prev,
        [updateLinkId]: [],
      }));
    } else {
      await createLink.mutateAsync(payload);
    }

    queryClient.invalidateQueries(queryKeys.links(section, uuid));

    setType(LinkTypeEnum.ARTICLE);
    setValue("");
  };

  const tType = (row: LinkListItem) => {
    return linkTypes.find((type) => type.value === row.type)?.label;
  };

  const tValue = (row: LinkListItem) => {
    return (
      <div className="flex align-items-center gap-2">
        <a href={row.value} target="_blank" rel="noreferrer">
          {row.value}
        </a>
        {row.type === LinkTypeEnum.VIMEO && (
          <Button
            icon="pi pi-file-o"
            type="button"
            size="small"
            severity="info"
            text={true}
            tooltip="Show Transcript"
            tooltipOptions={{ position: "top" }}
            onClick={() => showTranscript(row)}
          />
        )}
      </div>
    );
  };

  const tTags = (row: LinkListItem) => {
    return (
      <div className="flex flex-wrap gap-1">
        {(row.link_tags || []).map((tag) => (
          <span key={tag.id} className="p-chip p-chip-text">
            {tag.name}
          </span>
        ))}
      </div>
    );
  };

  const onUpdate = async (row: LinkListItem) => {
    setUpdateLinkId(row.id);
    setType(row.type);
    setValue(row.value);
    setTagsByLinkId((prev) => ({
      ...prev,
      [row.id]: row.link_tags || [],
    }));
  };

  const onDelete = async (id: string) => {
    await deleteLink.mutateAsync(id);
    queryClient.invalidateQueries(queryKeys.links(section, uuid));
    setType(LinkTypeEnum.ARTICLE);
    setValue("");
  };

  const tActions = (row: LinkListItem) => {
    return (
      <>
        <Button
          icon="pi pi-pencil"
          type="button"
          size="small"
          severity="secondary"
          text={true}
          onClick={() => onUpdate(row)}
        />
        <Button
          icon="pi pi-times"
          type="button"
          size="small"
          severity="danger"
          text={true}
          onClick={() => onDelete(row.id)}
        />
      </>
    );
  };

  return (
    <>
      <DataTable
        header="Links"
        value={links || []}
        emptyMessage={"No links"}
        stripedRows={true}
        size="small"
      >
        <Column header={"Type"} field="type" body={tType} />
        <Column header={"Value"} field="value" body={tValue} />
        {!isAccountManager() && <Column header={""} body={tActions} />}
        <Column header="Tags" body={tTags} />
      </DataTable>

      {!isAccountManager() && (
        <div className="grid formgrid mt-3">
          <div className="field col-12 md:col-4">
            <div className="p-float-label mt-3">
              <Dropdown
                options={linkTypeOptions}
                value={type}
                onChange={onTypeChange}
              />

              <label>Type *</label>
            </div>
          </div>
          <div className="field col-12 md:col-4">
            <div className="p-float-label mt-3">
              <InputText
                value={value}
                onChange={onLinkChange}
                placeholder="Value"
              />

              <label>Value *</label>
            </div>
          </div>
          {updateLinkId && (
            <div className="mt-3">
              <MultiSelect
                value={tagsByLinkId[updateLinkId] || []}
                onChange={onTagsChange}
                options={availableTags}
                optionLabel="name"
                display="chip"
                placeholder={`Select ${section.toLowerCase()} tags`}
                maxSelectedLabels={3}
                className="w-full"
              />
            </div>
          )}

          <div className="field col-12 md:col-4 pt-3">
            <Button
              label={updateLinkId ? "Update link" : "Add link"}
              disabled={!value || createLink.isLoading}
              onClick={onSubmit}
              type="button"
              className="w-auto"
              outlined={true}
              severity="secondary"
            />
          </div>
        </div>
      )}

      {selectedLinkForTranscript && (
        <TranscriptModal
          linkId={selectedLinkForTranscript.id}
          linkTitle={selectedLinkForTranscript.value}
          visible={transcriptModalVisible}
          onHide={hideTranscriptModal}
        />
      )}
    </>
  );
};

interface Props {
  uuid: string;
  section: LinkSectionEnum;
  links: LinkListItem[];
  availableTags: TagListItem[];
}
